/**
 * Lesson 3: The Order Book
 *
 * Introduces bids and asks, the order book visualization,
 * and concepts of market depth.
 */

import type { Lesson, SimulationScenario } from '../manager'
import type { Market, OrderBook, Order } from '../../engine/types'

// Helper to create an order
function createOrder(
  id: string,
  side: 'buy' | 'sell',
  price: number,
  quantity: number,
  timestamp: number
): Order {
  return {
    id,
    side,
    type: 'limit',
    price,
    quantity,
    timestamp,
    status: 'open',
    filledQuantity: 0,
  }
}

// Create a sample order book with realistic bid/ask distribution
const sampleOrderBook: OrderBook = {
  bids: [
    createOrder('bid-1', 'buy', 0.62, 50, Date.now() - 5000),
    createOrder('bid-2', 'buy', 0.61, 80, Date.now() - 4000),
    createOrder('bid-3', 'buy', 0.60, 120, Date.now() - 3000),
    createOrder('bid-4', 'buy', 0.58, 60, Date.now() - 2000),
    createOrder('bid-5', 'buy', 0.55, 100, Date.now() - 1000),
  ],
  asks: [
    createOrder('ask-1', 'sell', 0.65, 40, Date.now() - 5000),
    createOrder('ask-2', 'sell', 0.66, 70, Date.now() - 4000),
    createOrder('ask-3', 'sell', 0.68, 90, Date.now() - 3000),
    createOrder('ask-4', 'sell', 0.70, 50, Date.now() - 2000),
    createOrder('ask-5', 'sell', 0.75, 80, Date.now() - 1000),
  ],
  trades: [],
  lastPrice: 0.63,
}

const sampleMarket: Market = {
  id: 'lesson-3-market',
  question: 'Will the home team win tonight?',
  description: 'A prediction market for learning about order books',
  outcomes: ['Yes', 'No'] as const,
  orderBook: sampleOrderBook,
  resolved: false,
  resolution: null,
}

export const orderBookScenario: SimulationScenario = {
  id: 'order-book-intro',
  name: 'Order Book Introduction',
  description: 'A market with visible bids and asks to explore',
  market: sampleMarket,
  initialOrderBook: sampleOrderBook,
  userStartingBalance: 100,
}

export const lesson3: Lesson = {
  id: 'lesson-3',
  title: 'The Order Book',
  description: 'Understand how orders are organized and displayed',
  order: 3,
  prerequisites: ['lesson-2'],
  completionCriteria: { type: 'interactive-complete' },
  objectives: [
    'Understand what bids and asks are',
    'Read an order book visualization',
    'Identify the spread between buyers and sellers',
  ],
  content: [
    // Page 1: What is an Order Book?
    {
      type: 'text',
      data: {
        markdown: `# The Order Book

An **order book** is where all buy and sell orders live. It's the heart of any market!

## Two Sides of the Market

The order book has two sides:

### Bids (Buy Orders) 🟢
- People who want to **buy** shares
- They're "bidding" for shares
- Shown in **green** in our visualization
- Sorted highest price first (best bid on top)

### Asks (Sell Orders) 🔴
- People who want to **sell** shares
- They're "asking" a price for their shares
- Shown in **red** in our visualization
- Sorted lowest price first (best ask on top)

> **Remember**: Bids are buyers, Asks are sellers.`,
        visualizationId: 'order-book',
      },
    },

    // Page 2: Reading the Order Book
    {
      type: 'text',
      data: {
        markdown: `## Reading the Order Book

Look at the order book visualization. You'll see:

### Price Levels
Each horizontal bar represents a **price level**—a specific price where people want to trade.

### Quantity (Depth)
The **length of the bar** shows how many shares are available at that price.
- Longer bars = more shares available
- This is called **depth** or **liquidity**

### Best Bid and Best Ask
- **Best Bid**: The highest price any buyer will pay (top of green bars)
- **Best Ask**: The lowest price any seller will accept (top of red bars)

In our example:
- Best Bid: **$0.62** (someone will buy at this price)
- Best Ask: **$0.65** (someone will sell at this price)`,
        visualizationId: 'order-book',
      },
    },

    // Page 3: The Spread
    {
      type: 'text',
      data: {
        markdown: `## The Spread

The **spread** is the gap between the best bid and best ask.

### Calculating the Spread

**Spread = Best Ask - Best Bid**

In our example:
- Best Ask: $0.65
- Best Bid: $0.62
- **Spread: $0.03** (3 cents)

### What the Spread Tells You

| Spread | Market Condition |
|--------|------------------|
| Small (1-2¢) | Very active, easy to trade |
| Medium (3-5¢) | Normal activity |
| Large (5¢+) | Less active, harder to trade |

A **tight spread** means buyers and sellers almost agree on price.
A **wide spread** means there's disagreement—or not many traders.

> **The spread is like the "cost" of trading immediately.** If you buy at the ask and immediately sell at the bid, you lose the spread.`,
        visualizationId: 'order-book',
      },
    },

    // Page 4: Interactive exploration
    {
      type: 'interactive',
      data: {
        scenario: orderBookScenario,
        instructions: `## Explore the Order Book

Look at the order book visualization on the right. Try to identify:

1. **The best bid** (highest green bar)
2. **The best ask** (lowest red bar)
3. **The spread** (gap between them)
4. **Where the most liquidity is** (longest bars)

**Click on a price level** to see details about the orders there.

When you're ready, click "Mark as Complete" to continue.`,
        hints: [
          'Green bars are buy orders (bids). The topmost green bar is the best bid.',
          'Red bars are sell orders (asks). The topmost red bar is the best ask.',
          'The spread is the gap in the middle where there are no bars.',
        ],
        successCondition: () => true, // Manual completion for exploration
      },
    },

    // Quiz
    {
      type: 'quiz',
      data: {
        question: 'If the best bid is $0.55 and the best ask is $0.58, what is the spread?',
        options: [
          { id: 'a', text: '$0.03' },
          { id: 'b', text: '$0.55' },
          { id: 'c', text: '$0.58' },
          { id: 'd', text: '$1.13' },
        ],
        correctOptionId: 'a',
        explanation:
          'The spread is the difference between the best ask and the best bid: $0.58 - $0.55 = $0.03 (3 cents). This is the gap between what buyers will pay and what sellers want.',
      },
    },
  ],
}
